import { ImageIcon } from '@music-vine/cadence/icons';
import Autoplay from 'embla-carousel-autoplay';
import Image from 'next/image';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Carousel, type CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@music-vine/cadence/ui';
import { CarouselDots } from '@music-vine/cadence/ui';
import { TILE_LAYOUT_CLASSES_DOUBLE_WIDTH_BASIS, TILE_LAYOUT_CLASSES_NORMAL_WIDTH_BASIS, TILE_LAYOUT_CLASSES_PADDING } from '@/constants/tileGridLayout';
import { usePlaceholderImage } from '@/hooks/media/usePlaceholderImage';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { cn } from '@/lib/utils';
import { Tile, TileImage, TileSimilar } from './ui/tile';
import type { CSSProperties } from 'react';
import { cva } from 'class-variance-authority';
interface TileCarouselContextType {
  tileImageRef: React.RefObject<HTMLDivElement>;
  tileRef: React.RefObject<HTMLAnchorElement>;
}
const TileCarouselContext = createContext<TileCarouselContextType | null>(null);
const useTileCarouselContext = () => {
  const context = useContext(TileCarouselContext);
  if (!context) {
    throw new Error('TileCarousel components must be used within a TileCarouselProvider');
  }
  return context;
};

// Prev/next edge strip: callers set the veil/gradient and repeat it under
// `enabled:hover:` so it never swaps to a solid fill (DEV-7339).
export const carouselEdgeArrowClassName = 'text-gray-950 disabled:pointer-events-none dark:text-white [&_svg]:size-5 [&_svg]:transition-transform [&_svg]:duration-300 enabled:hover:[&_svg]:scale-150';
const seekClassNames = cva(
// Width spans the gutter plus the edge tile's -mx-3 hover-card overhang, so
// that tile never shows its hover state under the arrow.
cn('z-1 hidden w-[calc(var(--spacing-container)+0.75rem)] rounded-none opacity-0 transition-all duration-300 active:scale-100 disabled:opacity-0 enabled:group-hover/carousel:opacity-100 sm:flex', carouselEdgeArrowClassName), {
  variants: {
    variant: {
      default: 'h-[calc(100%+2px)] bg-white/70 pb-[var(--content-offset)] enabled:hover:bg-white/70 dark:bg-gray-950/70 dark:enabled:hover:bg-gray-950/70',
      globalSearch: 'h-[calc(100%+2px)] bg-white/70 pb-[var(--content-offset)] enabled:hover:bg-white/70 dark:bg-gray-875/80 dark:enabled:hover:bg-gray-875/80',
      'featured-banner': 'top-1/2 h-[7.5rem] -translate-y-1/2 bg-white/70 enabled:hover:bg-white/70 dark:bg-gray-850/70 dark:enabled:hover:bg-gray-850/70'
    },
    direction: {
      previous: 'left-0',
      next: 'right-0'
    }
  },
  compoundVariants: [{
    variant: 'featured-banner',
    direction: 'previous',
    class: 'rounded-[0_1.25rem_1.25rem_0]'
  }, {
    variant: 'featured-banner',
    direction: 'next',
    class: 'rounded-[1.25rem_0_0_1.25rem]'
  }]
});
interface TileCarouselProps {
  autoplay?: boolean;
  autoplayDelay?: number;
  children: React.ReactNode;
  className?: string;
  options?: {
    loop?: boolean;
    containScroll?: false | 'trimSnaps' | 'keepSnaps';
  };
  showDots?: boolean;
  variant?: 'default' | 'featured-banner' | 'globalSearch';
}
export const TileCarousel: React.FC<TileCarouselProps> = ({
  children,
  className,
  variant = 'default',
  options,
  autoplay = false,
  autoplayDelay = 4000,
  showDots = false
}) => {
  const [api, setApi] = useState<CarouselApi>();
  const tileRef = useRef<HTMLAnchorElement>(null!);
  const tileImageRef = useRef<HTMLDivElement>(null!);
  const [contentOffset, setContentOffset] = useState(0);
  const {
    isMd
  } = useTWScreens();
  const autoplayRef = useRef(autoplay ? [Autoplay({
    delay: autoplayDelay,
    stopOnInteraction: true,
    stopOnMouseEnter: true,
    stopOnFocusIn: true
  })] : []);

  // Set default options based on variant
  const carouselOptions = useMemo(() => {
    const defaultOptions = {
      slidesToScroll: 'auto' as const,
      dragFree: variant === 'featured-banner' ? false : !isMd,
      // Enable smooth infinite loop for featured banners
      loop: variant === 'featured-banner',
      ...options
    };
    return defaultOptions;
  }, [variant, isMd, options]);
  useEffect(() => {
    if (tileRef.current && tileImageRef.current) {
      const totalHeight = tileRef.current.clientHeight;
      const imageHeight = tileImageRef.current.clientHeight;
      const offset = totalHeight - imageHeight;
      setContentOffset(offset);
    }
  }, []);
  useEffect(() => {
    if (!api) {
      return () => {};
    }
    return () => {
      api.destroy();
    };
  }, [api]);
  const contextValue = useMemo(() => ({
    tileRef,
    tileImageRef
  }), []);
  const childrenArray = React.Children.toArray(children);
  return <TileCarouselContext.Provider value={contextValue} data-sentry-element="TileCarouselContext.Provider" data-sentry-component="TileCarousel" data-sentry-source-file="TileCarousel.tsx">
      <div className={cn('group/carousel @container relative', className)} onMouseEnter={() => {
      if (autoplay) {
        autoplayRef.current[0].stop();
      }
    }} onMouseLeave={() => {
      if (autoplay) {
        autoplayRef.current[0].play();
      }
    }} style={variant === 'featured-banner' ? undefined : {
      '--content-offset': `${contentOffset}px`
    } as CSSProperties}>
        <Carousel opts={carouselOptions} plugins={autoplayRef.current} setApi={setApi} data-sentry-element="Carousel" data-sentry-source-file="TileCarousel.tsx">
          <CarouselContent className={cn('mx-container', variant === 'featured-banner' ? 'mr-0 ml-0 md:ml-container' : 'mr-container ml-container', variant === 'globalSearch' && 'mr-4 max-md:mx-4! md:mx-container')} data-sentry-element="CarouselContent" data-sentry-source-file="TileCarousel.tsx">
            {children}
          </CarouselContent>
          <CarouselPrevious className={cn(seekClassNames({
          variant,
          direction: 'previous'
        }))} tabIndex={-1} variant="transparent" data-sentry-element="CarouselPrevious" data-sentry-source-file="TileCarousel.tsx" />
          <CarouselNext className={cn(seekClassNames({
          variant,
          direction: 'next'
        }))} tabIndex={-1} variant="transparent" data-sentry-element="CarouselNext" data-sentry-source-file="TileCarousel.tsx" />
        </Carousel>
        {showDots && <CarouselDots api={api} className="mt-4" count={childrenArray.length} />}
      </div>
    </TileCarouselContext.Provider>;
};
interface BaseTileCarouselItemProps {
  alt?: string;
  children: React.ReactNode;
  className?: string;
  gradientClass?: string;
  /** Toggle the tile's hover background. Defaults to true. */
  hover?: boolean;
  href?: string;
  imgAnchor?: 'left' | 'right' | 'default';
  imgOffsetClass?: string;
  imgTranslateClass?: string;
  index: number;
  invertTextColor?: boolean;
  onClick?: () => void;
}
interface DefaultTileCarouselItemProps extends BaseTileCarouselItemProps {
  src: string;
  variant?: 'default';
}
interface SimilarTileCarouselItemProps extends BaseTileCarouselItemProps {
  artistName: string;
  energy: number;
  leftImage?: string;
  mainImage: string;
  rightImage?: string;
  trackId: number;
  trackName: string;
  variant: 'similar';
}
interface CreatorPicksTileCarouselItemProps extends BaseTileCarouselItemProps {
  creatorImage?: string;
  creatorName?: string;
  src: string;
  variant: 'creator-picks';
}
interface FeaturedBannerTileCarouselItemProps extends BaseTileCarouselItemProps {
  backgroundColor: string;
  contentType: string;
  src: string;
  subtitle: string;
  title: string;
  variant: 'featured-banner';
}
interface RoundedImgTileCarouselItemProps extends BaseTileCarouselItemProps {
  src: string;
  variant: 'rounded-img';
}
interface TallTileCarouselItemProps extends BaseTileCarouselItemProps {
  src: string;
  variant: 'tall';
}
interface WideTileCarouselItemProps extends BaseTileCarouselItemProps {
  src: string;
  variant: 'wide';
}
interface VideoTileCarouselItemProps extends BaseTileCarouselItemProps {
  src?: string;
  variant: 'video';
}
type TileCarouselItemProps = DefaultTileCarouselItemProps | SimilarTileCarouselItemProps | CreatorPicksTileCarouselItemProps | FeaturedBannerTileCarouselItemProps | RoundedImgTileCarouselItemProps | TallTileCarouselItemProps | WideTileCarouselItemProps | VideoTileCarouselItemProps;
export const TileCarouselItem: React.FC<TileCarouselItemProps> = ({
  className,
  children,
  index,
  href,
  alt = '',
  variant = 'default',
  hover = true,
  onClick,
  invertTextColor = false,
  imgAnchor = 'default',
  imgTranslateClass,
  gradientClass,
  imgOffsetClass,
  ...props
}) => {
  const {
    tileRef,
    tileImageRef
  } = useTileCarouselContext();
  const placeholder = usePlaceholderImage();
  const defaultProps = props as DefaultTileCarouselItemProps;
  const similarProps = props as SimilarTileCarouselItemProps;
  const creatorPicksProps = props as CreatorPicksTileCarouselItemProps;
  const featuredBannerProps = props as FeaturedBannerTileCarouselItemProps;
  const roundedImgProps = props as RoundedImgTileCarouselItemProps;
  const tallProps = props as TallTileCarouselItemProps;
  const wideProps = props as WideTileCarouselItemProps;
  const videoProps = props as VideoTileCarouselItemProps;
  let carouselItemClasses = '';
  if (variant === 'featured-banner') {
    carouselItemClasses = 'basis-full pl-0 will-change-transform md:pr-container';
  } else {
    carouselItemClasses = `${variant === 'wide' ? TILE_LAYOUT_CLASSES_DOUBLE_WIDTH_BASIS : TILE_LAYOUT_CLASSES_NORMAL_WIDTH_BASIS} ${TILE_LAYOUT_CLASSES_PADDING} pl-0`;
  }
  if (!href) {
    return null;
  }
  return <CarouselItem className={cn(carouselItemClasses, className)} key={`tile-${index}`} data-sentry-element="CarouselItem" data-sentry-component="TileCarouselItem" data-sentry-source-file="TileCarousel.tsx">
      <Tile className={cn('shrink-0', {
      'h-[184px] md:h-[160px] lg:h-[200px] 2xl:h-[260px]': variant === 'featured-banner'
    })} hover={hover} href={href} onClick={onClick} ref={index === 0 ? tileRef : undefined} data-sentry-element="Tile" data-sentry-source-file="TileCarousel.tsx">
        {variant === 'default' && <TileImage alt={alt} className="bg-gray-100 dark:bg-gray-900" ref={index === 0 ? tileImageRef : undefined} src={defaultProps.src || placeholder} variant="default" />}
        {variant === 'similar' && <TileSimilar alt={alt} artistName={similarProps.artistName} energy={similarProps.energy} leftImage={similarProps.leftImage} mainImage={similarProps.mainImage} ref={index === 0 ? tileImageRef : undefined} rightImage={similarProps.rightImage} trackId={similarProps.trackId} trackName={similarProps.trackName} />}
        {variant === 'rounded-img' && <TileImage alt={alt} className="bg-gray-100 dark:bg-gray-900" ref={index === 0 ? tileImageRef : undefined} src={roundedImgProps.src || placeholder} variant="artist" />}
        {variant === 'tall' && <TileImage alt={alt} aspectRatio="tall" className="bg-gray-100 dark:bg-gray-900" ref={index === 0 ? tileImageRef : undefined} src={tallProps.src || placeholder} variant="tall" />}
        {variant === 'wide' && <TileImage alt={alt} aspectRatio="wide" className="bg-gray-100 dark:bg-gray-900" ref={index === 0 ? tileImageRef : undefined} sizes="(max-width: 539px) 300px, (max-width: 899px) 340px, (max-width: 1149px) 360px, (max-width: 1399px) 390px, (max-width: 1579px) 420px, 440px" src={wideProps.src || placeholder} variant="wide" />}
        {variant === 'video' && <div className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-xl bg-gray-100 transition group-focus-visible:ring-2 group-focus-visible:ring-pink-500 group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-white dark:bg-gray-800 dark:group-focus-visible:ring-offset-gray-950" ref={index === 0 ? tileImageRef : undefined}>
            {videoProps.src ? <Image alt={alt} className="object-cover" fill sizes="(max-width: 768px) 44vw, 44vw" src={videoProps.src} /> : <ImageIcon aria-hidden="true" className="size-8 text-gray-300 dark:text-gray-600" />}
          </div>}
        {variant === 'creator-picks' && <TileImage alt={alt} className="bg-gray-100 dark:bg-gray-900" ref={index === 0 ? tileImageRef : undefined} src={creatorPicksProps.src} variant="default" />}
        {variant === 'featured-banner' ? <div className="relative h-full w-full overflow-hidden bg-[var(--bg-color)] sm:rounded-[1rem]" style={{
        '--bg-color': featuredBannerProps.backgroundColor
      } as CSSProperties}>
            <div className={cn('absolute inset-0 right-0 h-full w-full', imgOffsetClass)}>
              <TileImage alt={alt} imgClassName={cn('object-cover', imgAnchor === 'default' && '@5xl:object-left object-center', imgAnchor === 'left' && 'object-left', imgAnchor === 'right' && 'object-right', imgTranslateClass)} sizes="80vw" src={featuredBannerProps.src} variant="featured-banner" />
              <div className={cn('absolute top-0 left-0 h-full transform bg-gradient-to-r from-[var(--bg-color)] to-transparent', gradientClass ?? '@5xl:w-[220px] w-1/3 @5xl:opacity-30 sm:w-3/4 md:w-7/12')} />
            </div>
            <div className="relative flex h-full w-full flex-col px-7 py-5 text-left sm:w-auto md:px-6 lg:p-8 2xl:px-10">
              <div className="inline-block w-fit rounded-tl-2xl rounded-tr rounded-br-2xl rounded-bl bg-gradient-to-r from-pink-500 to-pink-600 px-3 py-1 font-sans font-semibold text-white text-xs uppercase tracking-wider drop-shadow-md md:hidden">
                {featuredBannerProps.contentType}
              </div>
              <h2 className={cn('my-1 whitespace-nowrap font-semibold text-3xl drop-shadow-md xl:my-2', invertTextColor ? 'text-gray-950' : 'text-white')}>
                {featuredBannerProps.title}
              </h2>
              <p className={cn('@7xl:line-clamp-3 line-clamp-2 @md:max-w-md max-w-xs text-pretty @2xl:text-lg @xl:text-base text-sm text-white drop-shadow-md', invertTextColor ? 'text-gray-950' : 'text-white')}>
                {featuredBannerProps.subtitle}
              </p>
              {children}
            </div>
          </div> : children}
      </Tile>
    </CarouselItem>;
};